/**
 * Default community landing preference — persisted in localStorage,
 * namespaced per user so multiple accounts on the same browser do not
 * leak each other's preferences (DEV-1019).
 *
 * §7.9 Start Page (renamed 2026-07-13; formerly "Default Community View" —
 * the Communities nav item it referenced is retired). Controls where the
 * user LANDS on sign-in (the post-auth "/" redirect, the pref's only
 * consumer — the LeftNav is static per §7.9.8):
 *   null                → factory default: the default community's feed
 *                         (resolver chain: inviting org → home-geo walk).
 *   'feed'              → explicit form of the same.
 *   'action_center' | 'my_impact' | 'discover' → that surface.
 *   'overview'          → legacy: the Communities browse page.
 *   '{community-guid}'  → land on that community.
 *
 * Background. Before DEV-1019 this preference lived under a single
 * unscoped key, so when User A and User B used the same browser, B's
 * navigation defaulted to A's last picked community. The fix namespaces
 * the key by user GUID so each account's preference is isolated.
 */

import { apiFetch } from '../api/client';

const LS_KEY_BASE = 'l4e_default_community_view';

function keyFor(userGuid?: string | null): string {
  const g = String(userGuid || '').trim().toUpperCase();
  return g ? `${LS_KEY_BASE}:${g}` : LS_KEY_BASE;
}

/**
 * Read the saved default community view for a user.
 * Returns null when no explicit preference has been set so callers can
 * apply a fallback (home community → overview).
 */
export function getDefaultCommunityView(userGuid?: string | null): string | null {
  try {
    const val = window?.localStorage?.getItem(keyFor(userGuid));
    if (val && val.trim()) return val;
  } catch { /* SSR / privacy mode */ }
  return null;
}

/** Persist the user's chosen default community view, namespaced by user GUID. */
export function setDefaultCommunityView(value: string, userGuid?: string | null): void {
  try {
    window?.localStorage?.setItem(keyFor(userGuid), value);
  } catch { /* ignore */ }
}

/**
 * Remove the saved preference for a user. Equivalent to "reset to default
 * behavior" — LeftNav will fall back to the user's home community.
 */
export function clearDefaultCommunityView(userGuid?: string | null): void {
  try {
    window?.localStorage?.removeItem(keyFor(userGuid));
  } catch { /* ignore */ }
}

/* ── DEV-1448: the pref is SERVER-BACKED (security_users.default_community_view,
   migration 269). localStorage above is demoted to an optimistic per-device
   cache: reads stay synchronous for render paths, the server value is the
   truth and re-syncs the cache on load. Cross-device lesson from DEV-1443. */

/** Persist the pref to the server (profile-update path). Pass null to clear. */
export async function updateDefaultCommunityViewOnServer(
  value: string | null,
  userGuid: string,
): Promise<void> {
  await apiFetch(`/api/security/users/${userGuid}/default-community-view`, {
    method: 'PUT',
    body: JSON.stringify({ defaultCommunityView: value }),
  });
}

/**
 * Reconcile the local cache with the authoritative server value (call when
 * the security row loads). Null clears the cache so a pref removed on
 * another device doesn't linger here.
 */
export function syncDefaultCommunityViewCache(
  serverValue: string | null | undefined,
  userGuid?: string | null,
): void {
  if (serverValue && serverValue.trim()) {
    setDefaultCommunityView(serverValue, userGuid);
  } else {
    clearDefaultCommunityView(userGuid);
  }
}
